import * as THREE from 'three';
import {
  PerspectiveCamera,
  OrthographicCamera,
  Object3D,
  Group,
  Mesh,
  Vector3,
  Box3,
  WebGLRenderer,
  Scene,
  Raycaster,
} from 'three';
import CameraControls from 'camera-controls';
import gsap from 'gsap';

CameraControls.install({ THREE });

/**
 * 카메라 타입 정의
 */
export type CameraType = 'perspective' | 'orthographic';

/**
 * 카메라 정보 인터페이스
 */
export interface CameraInfo {
  id: string;
  name: string;
  camera: PerspectiveCamera | OrthographicCamera;
  type: CameraType;
  position: Vector3;
  target: Vector3;
  fov?: number;
  near: number;
  far: number;
  aspect: number;
  visible: boolean;
  userData: any;
}

/**
 * 줌인 설정 인터페이스
 */
export interface ZoomInSettings {
  duration: number;
  ease: string;
  padding: number;
  maxDistance: number;
  collisionCheck: boolean;
}

/**
 * 카메라 이동 설정 인터페이스
 */
export interface CameraMoveSettings {
  duration: number;
  ease: string;
  delay: number;
}

/**
 * 캡쳐 설정 인터페이스
 */
export interface CaptureSettings {
  width?: number;
  height?: number;
  format?: 'image/png' | 'image/jpeg';
  quality?: number;
  antialias?: boolean;
}

export interface ScreenshotSettings {
  direction?: 'front' | 'back' | 'left' | 'right' | 'top';
  isAnimation?: boolean;
  delay?: number;
}

/**
 * 카메라를 제어하고 관리하는 클래스
 */
export class CameraManager {
  private _hideCameraCanvas: HTMLCanvasElement | null = null;

  // 카메라 관리
  private _cameras: Map<string, CameraInfo> = new Map();
  private _cameraCounter: number = 0;

  // 현재 활성 카메라
  private _activeCameraId: string | null = null;
  private _activeCamera: PerspectiveCamera | OrthographicCamera | null = null;

  private _frontCamera: THREE.OrthographicCamera | undefined;
  private _backCamera: THREE.OrthographicCamera | undefined;
  private _topCamera: THREE.OrthographicCamera | undefined;
  private _leftCamera: THREE.OrthographicCamera | undefined;
  private _rightCamera: THREE.OrthographicCamera | undefined;

  // 카메라 컨트롤
  private _cameraControls: CameraControls | null = null;
  private _cameraControlsEnabled: boolean = true;

  // Scene 및 Renderer 참조
  private _scene: Scene | null = null;
  private _renderer: WebGLRenderer | null = null;

  // 줌인 설정
  private _zoomInSettings: ZoomInSettings;

  // 이벤트 콜백
  private _onCameraChanged?: (cameraInfo: CameraInfo) => void;

  // 자동 회전 관련 변수들
  private _autoRotate: boolean = true;
  private _autoRotateSpeed: number = 2.0;
  private _cameraRotation: { x: number; y: number } = { x: 0, y: 0 };
  private _targetPosition: THREE.Vector3 = new THREE.Vector3(0, 1, 0);
  private _autoRotateDistance: number = 20;

  // 마우스/터치 제어 관련 변수들
  private _isMouseDown: boolean = false;
  private _previousMousePosition: { x: number; y: number } = { x: 0, y: 0 };
  private _onCameraMoved?: (cameraInfo: CameraInfo) => void;
  private _onZoomInComplete?: (target: Object3D) => void;

  constructor() {
    // 기본 줌인 설정
    this._zoomInSettings = {
      duration: 2.0,
      ease: 'power2.out',
      padding: 1.2,
      maxDistance: 100,
      collisionCheck: true,
    };
    this._hideCameraCanvas = document.createElement('canvas');

    // 기본 카메라 생성
    this.createDefaultCameras();
  }

  /**
   * 기본 카메라 생성
   */
  private createDefaultCameras(): void {
    // 렌더링용 원근 카메라
    const perspectiveCamera = new THREE.PerspectiveCamera(
      26.9, // FOV
      16 / 9, // Aspect ratio
      0.1, // Near
      1000, // Far
    );
    perspectiveCamera.position.set(20, 10, 20);
    perspectiveCamera.lookAt(0, 0, 0);

    this._activeCamera = perspectiveCamera;

    this.addCamera(perspectiveCamera, 'perspective', 'MainCamera');

    // 전방 카메라 (Z축 양의 방향)
    this._frontCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    this._frontCamera.name = 'FrontCamera';
    this.addCamera(this._frontCamera, 'orthographic', 'FrontCamera');

    // 후방 카메라 (Z축 음의 방향)
    this._backCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    this._backCamera.name = 'BackCamera';
    this.addCamera(this._backCamera, 'orthographic', 'BackCamera');

    // 상단 카메라 (Y축 양의 방향)
    this._topCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    this._topCamera.name = 'TopCamera';
    this.addCamera(this._topCamera, 'orthographic', 'TopCamera');

    this._rightCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    this._rightCamera.name = 'RightCamera';
    this.addCamera(this._rightCamera, 'orthographic', 'RightCamera');

    // 좌측 카메라 (X축 음의 방향)
    this._leftCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
    this._leftCamera.name = 'LeftCamera';
    this.addCamera(this._leftCamera, 'orthographic', 'LeftCamera');
  }

  /**
   * Scene 및 Renderer 설정
   */
  public setSceneAndRenderer(scene: Scene, renderer: WebGLRenderer): void {
    this._scene = scene;
    this._renderer = renderer;
  }

  /**
   * 카메라 추가
   */
  public addCamera(
    camera: PerspectiveCamera | OrthographicCamera,
    type: CameraType,
    name?: string,
  ): string {
    const id = this.generateCameraId();
    console.log(id);
    const cameraName = name || `${type}_${id}`;

    let fov: number | undefined;
    let near: number = 0.1;
    let far: number = 1000;
    let aspect: number = 16 / 9;

    if (camera instanceof PerspectiveCamera) {
      fov = camera.fov;
      near = camera.near;
      far = camera.far;
      aspect = camera.aspect;
    } else if (camera instanceof OrthographicCamera) {
      near = camera.near;
      far = camera.far;
      aspect = (camera.right - camera.left) / (camera.top - camera.bottom);
    }

    const cameraInfo: CameraInfo = {
      id,
      name: cameraName,
      camera,
      type,
      position: camera.position.clone(),
      target: new Vector3(),
      fov: fov ?? 45, // 기본값 45 제공
      near,
      far,
      aspect,
      visible: camera.visible,
      userData: {},
    };

    // 타겟 위치 계산
    if (camera instanceof PerspectiveCamera) {
      const direction = new Vector3(0, 0, -1);
      direction.applyQuaternion(camera.quaternion);
      cameraInfo.target.copy(camera.position).add(direction);
    }

    this._cameras.set(id, cameraInfo);

    // Scene에 카메라 추가
    if (this._scene) {
      this._scene.add(camera);
    }

    return id;
  }

  /**
   * 카메라 제거
   */
  public removeCamera(cameraId: string): boolean {
    const cameraInfo = this._cameras.get(cameraId);
    if (!cameraInfo) return false;

    // 활성 카메라인 경우 처리
    if (this._activeCameraId === cameraId) {
      this._activeCameraId = null;
      this._activeCamera = null;
    }

    // Scene에서 제거
    if (this._scene) {
      this._scene.remove(cameraInfo.camera);
    }

    this._cameras.delete(cameraId);
    return true;
  }

  /**
   * 활성 카메라 설정
   */
  public setActiveCamera(cameraId: string): boolean {
    const cameraInfo = this._cameras.get(cameraId);
    if (!cameraInfo) return false;

    this._activeCameraId = cameraId;
    this._activeCamera = cameraInfo.camera;

    // 카메라 컨트롤 업데이트
    if (this._cameraControls) {
      this._cameraControls.dispose();
    }

    if (this._onCameraChanged) {
      this._onCameraChanged(cameraInfo);
    }

    return true;
  }

  /**
   * 활성 카메라 가져오기
   */
  public getActiveCamera(): PerspectiveCamera | OrthographicCamera | null {
    return this._activeCamera;
  }

  /**
   * 활성 카메라 정보 가져오기
   */
  public getActiveCameraInfo(): CameraInfo | undefined {
    if (!this._activeCameraId) return undefined;
    return this._cameras.get(this._activeCameraId);
  }

  /**
   * 모든 카메라 정보 가져오기
   */
  public getAllCameras(): CameraInfo[] {
    return Array.from(this._cameras.values());
  }

  /**
   * 카메라 정보 가져오기
   */
  public getCamera(cameraId: string): CameraInfo | undefined {
    return this._cameras.get(cameraId);
  }

  /**
   * 카메라 이동 (GSAP 기반 부드러운 이동)
   */
  public moveCamera(
    cameraId: string,
    targetPosition: Vector3,
    targetLookAt?: Vector3,
    settings?: Partial<CameraMoveSettings>,
  ): Promise<void> {
    return new Promise((resolve) => {
      const cameraInfo = this._cameras.get(cameraId);
      if (!cameraInfo) {
        resolve();
        return;
      }

      const moveSettings: CameraMoveSettings = {
        duration: 2.0,
        ease: 'power2.out',
        delay: 0,
        ...settings,
      };

      const camera = cameraInfo.camera;
      const startPosition = camera.position.clone();
      const startLookAt = new Vector3();

      // 현재 바라보는 방향 계산
      if (camera instanceof PerspectiveCamera) {
        const direction = new Vector3(0, 0, -1);
        direction.applyQuaternion(camera.quaternion);
        startLookAt.copy(camera.position).add(direction);
      }

      const endLookAt = targetLookAt || startLookAt;

      // GSAP 애니메이션
      const timeline = gsap.timeline({
        onComplete: () => {
          this.updateCameraInfo();
          if (this._onCameraMoved) {
            this._onCameraMoved(cameraInfo);
          }
          resolve();
        },
      });

      // 위치 이동
      timeline.to(camera.position, {
        x: targetPosition.x,
        y: targetPosition.y,
        z: targetPosition.z,
        duration: moveSettings.duration,
        ease: moveSettings.ease,
        delay: moveSettings.delay,
      });

      // 바라보는 방향 변경
      if (targetLookAt) {
        timeline.to(
          {},
          {
            duration: moveSettings.duration,
            ease: moveSettings.ease,
            delay: moveSettings.delay,
            onUpdate: () => {
              camera.lookAt(endLookAt);
            },
          },
          0,
        );
      }
    });
  }

  /**
   * 오브젝트 중심으로 줌인
   */
  public async zoomInToObject(
    target: Object3D,
    settings?: Partial<ZoomInSettings>,
  ): Promise<boolean> {
    if (!this._activeCamera || !this._activeCameraId) return false;

    const zoomSettings = { ...this._zoomInSettings, ...settings };
    const cameraInfo = this._cameras.get(this._activeCameraId);
    if (!cameraInfo) return false;

    try {
      // 오브젝트의 바운딩 박스 계산
      const boundingBox = this.calculateBoundingBox(target);
      const center = boundingBox.getCenter(new Vector3());
      const size = boundingBox.getSize(new Vector3());

      // 카메라와 오브젝트 사이의 거리 계산
      const maxDimension = Math.max(size.x, size.y, size.z);
      const distance =
        (maxDimension * zoomSettings.padding) /
        (2 * Math.tan(((cameraInfo.fov || 45) * Math.PI) / 360));

      // 충돌 검사
      if (zoomSettings.collisionCheck) {
        const collisionDistance = this.checkCollision(center, distance);
        if (collisionDistance < distance) {
          // 충돌이 발생한 경우 안전한 거리로 조정
          const safeDistance = collisionDistance * 0.8;
          await this.moveCamera(
            this._activeCameraId,
            center.clone().add(new Vector3(0, 0, safeDistance)),
            center,
          );
        } else {
          await this.moveCamera(
            this._activeCameraId,
            center.clone().add(new Vector3(0, 0, distance)),
            center,
          );
        }
      } else {
        await this.moveCamera(
          this._activeCameraId,
          center.clone().add(new Vector3(0, 0, distance)),
          center,
        );
      }

      if (this._onZoomInComplete) {
        this._onZoomInComplete(target);
      }

      return true;
    } catch (error) {
      console.error('줌인 실패:', error);
      return false;
    }
  }

  /**
   * 바운딩 박스 계산
   */
  private calculateBoundingBox(object: Object3D): Box3 {
    const boundingBox = new Box3();

    if (object instanceof Mesh) {
      boundingBox.setFromObject(object);
    } else if (object instanceof Group) {
      object.traverse((child) => {
        if (child instanceof Mesh) {
          boundingBox.expandByObject(child);
        }
      });
    } else {
      boundingBox.setFromObject(object);
    }

    return boundingBox;
  }

  /**
   * 충돌 검사
   */
  private checkCollision(center: Vector3, distance: number): number {
    if (!this._scene) return distance;

    const rayDirection = new Vector3(0, 0, 1);
    const raycaster = new THREE.Raycaster();

    // 여러 방향에서 충돌 검사
    const directions = [
      new Vector3(0, 0, 1),
      new Vector3(0, 0, -1),
      new Vector3(1, 0, 0),
      new Vector3(-1, 0, 0),
      new Vector3(0, 1, 0),
      new Vector3(0, -1, 0),
    ];

    let minDistance = distance;

    directions.forEach((direction) => {
      raycaster.set(center, direction.normalize());
      const intersects = raycaster.intersectObjects(this._scene!.children, true);

      if (intersects.length > 0) {
        const intersectDistance = intersects[0].distance;
        if (intersectDistance < minDistance) {
          minDistance = intersectDistance;
        }
      }
    });

    return minDistance;
  }

  /**
   * 스크린샷 촬영
   */
  public capturePhoto(camera?: PerspectiveCamera | OrthographicCamera): string {
    if (!this._scene || !this._renderer || !this._activeCamera) return '';

    const captureSettings: CaptureSettings = {
      width: this._renderer.domElement.width,
      height: this._renderer.domElement.height,
      format: 'image/png',
      quality: 1.0,
      antialias: true,
    };

    // 고해상도 렌더링을 위한 임시 설정
    const originalPixelRatio = this._renderer.getPixelRatio();
    const originalSize = this._renderer.getSize(new THREE.Vector2());

    this._renderer.setPixelRatio(1);
    this._renderer.setSize(captureSettings.width!, captureSettings.height!, false);

    // 렌더링 수행
    this._renderer.render(this._scene, this._activeCamera);

    // 이미지 데이터 URL 생성
    const dataURL = this._renderer.domElement.toDataURL(
      captureSettings.format,
      captureSettings.quality,
    );

    // 원래 설정 복원
    this._renderer.setPixelRatio(originalPixelRatio);
    this._renderer.setSize(originalSize.width, originalSize.height, false);

    return dataURL;
  }

  /**
   * 다방향 스크린샷 촬영
   */
  public async captureAllDirections(object: Object3D): Promise<{
    front: string;
    back: string;
    left: string;
    right: string;
    top: string;
  }> {
    this.adjustCaptureCameras(object);

    if (!this._scene || !this._renderer)
      return {
        front: '',
        back: '',
        left: '',
        right: '',
        top: '',
      };

    if (
      !this._frontCamera ||
      !this._backCamera ||
      !this._leftCamera ||
      !this._rightCamera ||
      !this._topCamera
    )
      return {
        front: '',
        back: '',
        left: '',
        right: '',
        top: '',
      };

    const results = {
      front: await this.captureFromCamera(this._frontCamera, this._scene, this._renderer, object),
      back: await this.captureFromCamera(this._backCamera, this._scene, this._renderer, object),
      left: await this.captureFromCamera(this._leftCamera, this._scene, this._renderer, object),
      right: await this.captureFromCamera(this._rightCamera, this._scene, this._renderer, object),
      top: await this.captureFromCamera(this._topCamera, this._scene, this._renderer, object),
    };
    return results;
  }

  /**
   * 특정 카메라에서 촬영을 실행합니다.
   * @param camera 촬영할 카메라
   * @param angleName 각도 이름
   * @returns base64 이미지 데이터
   */
  private async captureFromCamera(
    camera: PerspectiveCamera | OrthographicCamera,
    scene: Scene,
    gl: WebGLRenderer,
    target: Object3D,
  ): Promise<string> {
    if (!scene || !this._hideCameraCanvas) return '';

    // 새로운 캔버스 요소 생성 (DOM에 추가하지 않음)
    // 카메라별로 고정된 해상도 설정
    let width: number, height: number;

    const box = new THREE.Box3().setFromObject(target);
    const size = box.getSize(new THREE.Vector3());
    const pixel = 2000;

    if (camera.name === 'FrontCamera' || camera.name === 'BackCamera') {
      const aspectRatio = size.x / size.y;
      if (aspectRatio > 1) {
        width = pixel;
        height = width / aspectRatio;
      } else {
        height = pixel;
        width = height * aspectRatio;
      }
    } else if (camera.name === 'TopCamera') {
      const aspectRatio = size.x / size.z;
      if (aspectRatio > 1) {
        width = pixel;
        height = width / aspectRatio;
      } else {
        height = pixel;
        width = height * aspectRatio;
      }
    } else if (camera.name === 'LeftCamera') {
      const aspectRatio = size.z / size.y;
      if (aspectRatio > 1) {
        width = pixel;
        height = width / aspectRatio;
      } else {
        height = pixel;
        width = height * aspectRatio;
      }
    } else {
      const aspectRatio = size.x / size.y;
      if (aspectRatio > 1) {
        width = pixel;
        height = width / aspectRatio;
      } else {
        height = pixel;
        width = height * aspectRatio;
      }
    }

    // 캔버스 크기 설정
    this._hideCameraCanvas.width = width;
    this._hideCameraCanvas.height = height;

    // 새로운 렌더러 생성 (새 캔버스 사용)
    const renderer = new THREE.WebGLRenderer({
      canvas: this._hideCameraCanvas,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
      precision: 'highp',
      stencil: true,
      depth: true,
      premultipliedAlpha: true,
      preserveDrawingBuffer: true,
    });

    renderer.setSize(width, height);

    // 메인 렌더러와 완벽하게 동일한 환경 설정 적용

    // 렌더링 실행
    renderer.render(scene, camera);

    // base64로 변환
    const imageData = this._hideCameraCanvas.toDataURL('image/png');

    // 렌더러 정리
    renderer.dispose();

    return imageData;
  }

  /**
   * 촬영용 카메라들의 위치와 시야를 오브젝트에 맞게 조정합니다.
   */
  private adjustCaptureCameras(target: Object3D): void {
    if (!target.children.length) return;

    // 메인 그룹의 바운딩 박스 계산
    const boundingBox = new THREE.Box3().setFromObject(target);
    const size = boundingBox.getSize(new THREE.Vector3());
    const center = boundingBox.getCenter(new THREE.Vector3());

    // 오브젝트의 최대 크기 계산 - 카메라 거리를 더 가깝게 설정
    const maxDimension = Math.max(size.x, size.y, size.z);
    const cameraDistance = maxDimension * 0.8; // 여백을 0.8배로 줄임 (기존 1.5배에서)

    // 각 카메라의 위치와 시야 조정
    if (this._frontCamera) {
      this._frontCamera.position.set(center.x, center.y, center.z + cameraDistance);
      this._frontCamera.lookAt(center);
      this.adjustOrthographicCamera(this._frontCamera, size, maxDimension);
    }

    if (this._backCamera) {
      this._backCamera.position.set(center.x, center.y, center.z - cameraDistance);
      this._backCamera.lookAt(center);
      this.adjustOrthographicCamera(this._backCamera, size, maxDimension);
    }

    if (this._topCamera) {
      this._topCamera.position.set(center.x, center.y + cameraDistance, center.z);
      this._topCamera.lookAt(center);
      this.adjustOrthographicCamera(this._topCamera, size, maxDimension);
    }

    if (this._rightCamera) {
      this._rightCamera.position.set(center.x - cameraDistance, center.y, center.z);
      this._rightCamera.lookAt(center);
      this.adjustOrthographicCamera(this._rightCamera, size, maxDimension);
    }

    if (this._leftCamera) {
      this._leftCamera.position.set(center.x + cameraDistance, center.y, center.z);
      this._leftCamera.lookAt(center);
      this.adjustOrthographicCamera(this._leftCamera, size, maxDimension);
    }
  }

  private adjustOrthographicCamera(
    camera: THREE.OrthographicCamera,
    size: THREE.Vector3,
    maxDimension: number,
  ): void {
    let halfWidth: number;
    let halfHeight: number;

    if (camera.name === 'FrontCamera' || camera.name === 'BackCamera') {
      halfWidth = size.x / 2;
      halfHeight = size.y / 2;
    } else if (camera.name === 'TopCamera') {
      halfWidth = size.x / 2;
      halfHeight = size.z / 2;
    } else if (camera.name === 'LeftCamera') {
      halfWidth = size.z / 2;
      halfHeight = size.y / 2;
    } else {
      halfWidth = size.x / 2;
      halfHeight = size.y / 2;
    }

    camera.left = -halfWidth;
    camera.right = halfWidth;
    camera.top = halfHeight;
    camera.bottom = -halfHeight;
    camera.near = 0.1;
    camera.far = 1000;

    camera.updateProjectionMatrix();
  }

  /**
   * 특정 방향으로 카메라 이동
   */
  public async lookAtDirection(
    direction: 'front' | 'back' | 'left' | 'right' | 'top',
    isAnimation: boolean = false,
  ): Promise<void> {
    if (!this._activeCamera) {
      console.warn('활성 카메라가 없습니다.');
      return;
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      const rotation = this.getRotationByDirection(direction);

      // 카메라 위치와 방향 설정
      const distance = 20;
      const x = Math.sin(rotation.y) * Math.cos(rotation.x) * distance;
      const y = Math.sin(rotation.x) * distance;
      const z = Math.cos(rotation.y) * Math.cos(rotation.x) * distance;

      this._activeCamera.position.set(x, y, z);
      this._activeCamera.lookAt(0, 0, 0);
    } catch (error) {
      console.warn('카메라 방향 이동 실패:', error);
    }
  }

  /**
   * 방향별 회전값 반환
   */
  private getRotationByDirection(direction: 'front' | 'back' | 'left' | 'right' | 'top'): {
    x: number;
    y: number;
  } {
    const DEG90 = Math.PI * 0.5;
    const DEG180 = Math.PI;

    switch (direction) {
      case 'front':
        return { x: 0, y: DEG90 };
      case 'back':
        return { x: DEG180, y: DEG90 };
      case 'left':
        return { x: DEG90, y: DEG90 };
      case 'right':
        return { x: -DEG90, y: DEG90 };
      case 'top':
        return { x: 0, y: 0 };
      default:
        return { x: 0, y: DEG90 };
    }
  }

  /**
   * 지연 함수
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * 줌인 설정 변경
   */
  public setZoomInSettings(settings: Partial<ZoomInSettings>): void {
    this._zoomInSettings = { ...this._zoomInSettings, ...settings };
  }

  /**
   * 줌인 설정 가져오기
   */
  public getZoomInSettings(): ZoomInSettings {
    return { ...this._zoomInSettings };
  }

  /**
   * 카메라 정보 업데이트
   */
  private updateCameraInfo(): void {
    if (!this._activeCameraId) return;

    const cameraInfo = this._cameras.get(this._activeCameraId);
    if (!cameraInfo) return;

    cameraInfo.position.copy(this._activeCamera!.position);

    if (this._activeCamera instanceof PerspectiveCamera) {
      const direction = new Vector3(0, 0, -1);
      direction.applyQuaternion(this._activeCamera.quaternion);
      cameraInfo.target.copy(this._activeCamera.position).add(direction);
    }
  }

  /**
   * 카메라 컨트롤 업데이트
   */
  public updateCameraControls(): void {
    if (this._cameraControls) {
      this._cameraControls.update(1);
    }
  }

  /**
   * 고유 카메라 ID 생성
   */
  private generateCameraId(): string {
    return `camera_${++this._cameraCounter}`;
  }

  /**
   * 이벤트 콜백 설정
   */
  public setOnCameraChanged(callback: (cameraInfo: CameraInfo) => void): void {
    this._onCameraChanged = callback;
  }

  public setOnCameraMoved(callback: (cameraInfo: CameraInfo) => void): void {
    this._onCameraMoved = callback;
  }

  public setOnZoomInComplete(callback: (target: Object3D) => void): void {
    this._onZoomInComplete = callback;
  }

  /**
   * 카메라 잠금 설정
   */
  public setCameraLocked(isLocked: boolean): void {
    if (!this._cameraControls) return;

    if (isLocked) {
      this._cameraControls.maxAzimuthAngle = 0;
      this._cameraControls.minAzimuthAngle = 0;
    } else {
      this._cameraControls.maxAzimuthAngle = Infinity;
      this._cameraControls.minAzimuthAngle = -Infinity;
    }
  }

  /**
   * 카메라 컨트롤 잠금
   */
  public holdCameraControls(): void {
    if (!this._cameraControls) return;

    // 카메라 컨트롤을 일시적으로 비활성화
    this._cameraControls.enabled = false;
  }

  /**
   * 카메라 컨트롤 해제
   */
  public releaseCameraControls(): void {
    if (!this._cameraControls) return;

    // 카메라 컨트롤을 다시 활성화
    this._cameraControls.enabled = true;
  }

  /**
   * 아지무스 회전
   */
  public rotateAzimuthTo(angle: number, isAnimation: boolean): Promise<void> {
    if (!this._activeCamera) {
      console.warn('활성 카메라가 없습니다.');
      return Promise.resolve();
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      const currentPosition = this._activeCamera.position.clone();
      const radius = Math.sqrt(currentPosition.x ** 2 + currentPosition.z ** 2);

      const newX = Math.cos(angle) * radius;
      const newZ = Math.sin(angle) * radius;

      this._activeCamera.position.set(newX, currentPosition.y, newZ);
      this._activeCamera.lookAt(0, 0, 0);

      return Promise.resolve();
    } catch (error) {
      console.warn('아지무스 회전 실패:', error);
      return Promise.resolve();
    }
  }

  /**
   * 폴라 회전
   */
  public rotatePolarTo(angle: number, isAnimation: boolean): Promise<void> {
    if (!this._activeCamera) {
      console.warn('활성 카메라가 없습니다.');
      return Promise.resolve();
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      const currentPosition = this._activeCamera.position.clone();
      const horizontalDistance = Math.sqrt(currentPosition.x ** 2 + currentPosition.z ** 2);

      const newY = Math.sin(angle) * 20; // 고정 거리 20 사용
      const newX = Math.cos(angle) * horizontalDistance;
      const newZ = Math.sin(angle) * horizontalDistance;

      this._activeCamera.position.set(newX, newY, newZ);
      this._activeCamera.lookAt(0, 0, 0);

      return Promise.resolve();
    } catch (error) {
      console.warn('폴라 회전 실패:', error);
      return Promise.resolve();
    }
  }

  /**
   * 박스에 맞춰 카메라 조정
   */
  public fitToBox(object: Object3D | null, isAnimation: boolean): Promise<void[] | false> {
    if (!this._activeCamera || !object) {
      console.warn('활성 카메라가 없거나 오브젝트가 없습니다.');
      return Promise.resolve(false);
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 2; // 오브젝트 크기의 2배 거리

      this._activeCamera.position.set(
        center.x + distance,
        center.y + distance,
        center.z + distance,
      );
      this._activeCamera.lookAt(center);

      return Promise.resolve([] as void[]);
    } catch (error) {
      console.warn('박스 맞춤 실패:', error);
      return Promise.resolve(false);
    }
  }

  /**
   * 구에 맞춰 카메라 조정
   */
  public fitToSphere(object: Object3D | null, isAnimation: boolean): Promise<any> {
    if (!this._activeCamera || !object) {
      console.warn('활성 카메라가 없거나 오브젝트가 없습니다.');
      return Promise.resolve(false);
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      const sphere = new THREE.Sphere();
      const box = new THREE.Box3().setFromObject(object);
      box.getBoundingSphere(sphere);

      const distance = sphere.radius * 3; // 구 반지름의 3배 거리

      this._activeCamera.position.set(
        sphere.center.x + distance,
        sphere.center.y + distance,
        sphere.center.z + distance,
      );
      this._activeCamera.lookAt(sphere.center);

      return Promise.resolve(true);
    } catch (error) {
      console.warn('구 맞춤 실패:', error);
      return Promise.resolve(false);
    }
  }

  /**
   * 카메라 위치 설정
   */
  public setPosition(x: number, y: number, z: number, isAnimation: boolean): Promise<void | false> {
    if (!this._activeCamera) {
      console.warn('활성 카메라가 없습니다.');
      return Promise.resolve(false);
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      this._activeCamera.position.set(x, y, z);
      return Promise.resolve();
    } catch (error) {
      console.warn('위치 설정 실패:', error);
      return Promise.resolve(false);
    }
  }

  /**
   * 카메라 타겟 설정
   */
  public setTarget(x: number, y: number, z: number, isAnimation: boolean): Promise<void | false> {
    if (!this._activeCamera) {
      console.warn('활성 카메라가 없습니다.');
      return Promise.resolve(false);
    }

    try {
      // 기본 Three.js 카메라 제어 사용
      this._activeCamera.lookAt(x, y, z);
      return Promise.resolve();
    } catch (error) {
      console.warn('타겟 설정 실패:', error);
      return Promise.resolve(false);
    }
  }

  /**
   * 모든 카메라 정리
   */
  public clearAllCameras(): void {
    this._cameras.forEach((cameraInfo) => {
      if (this._scene) {
        this._scene.remove(cameraInfo.camera);
      }
    });

    this._cameras.clear();
    this._activeCameraId = null;
    this._activeCamera = null;

    if (this._cameraControls) {
      this._cameraControls.dispose();
      this._cameraControls = null;
    }
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.clearAllCameras();

    this._scene = null;
    this._renderer = null;
  }

  /**
   * CameraControls 반환
   */
  public getCameraControls(): any {
    return this._cameraControls;
  }

  /**
   * CameraControls 초기화 및 설정
   */
  public initializeCameraControls(canvas: HTMLCanvasElement): void {
    try {
      if (!this._activeCamera || !canvas) return;

      // CameraControls 인스턴스 생성
      this._cameraControls = new CameraControls(this._activeCamera, canvas);

      // 기본 설정
      this._cameraControls.enabled = this._cameraControlsEnabled;

      // 카메라 제한 설정
      this._setupCameraConstraints();

      // 카메라 위치 초기화
      this._cameraControls.setPosition(4, 1, 3, false);
      this._cameraControls.setTarget(0, 1, 0, false);
    } catch (error) {
      console.error('CameraManager: CameraControls 초기화 중 오류 발생:', error);
      this._cameraControls = null;
    }
  }

  /**
   * 카메라 컨트롤 제한 사항 설정
   */
  private _setupCameraConstraints(): void {
    try {
      if (!this._cameraControls) return;

      // 1. 수직 각도 제한 (바닥 이하 방지)
      this._cameraControls.minPolarAngle = 0; // 0° (수평)
      this._cameraControls.maxPolarAngle = Math.PI / 2; // 180° (수평, 반대편)

      // 2. 줌 아웃 제한 (오브젝트가 너무 작아지지 않도록)
      this._cameraControls.minDistance = 2; // 최소 거리 (너무 가까이)
      this._cameraControls.maxDistance = 6; // 최대 거리 (너무 멀리)

      // 3. 부드러운 제한 적용
      this._cameraControls.dampingFactor = 0.05; // 제한에 도달했을 때 부드러운 정지
      this._cameraControls.draggingDampingFactor = 0.05; // 드래그 시 부드러운 움직임

      console.log('CameraManager: 카메라 제한 사항이 설정되었습니다.');
    } catch (error) {
      console.error('CameraManager: 카메라 제한 설정 중 오류 발생:', error);
    }
  }

  /**
   * 자동 회전 설정
   */
  public setupAutoRotation(): void {
    try {
      if (!this._activeCamera) return;

      // CameraControls가 있으면 CameraControls 사용
      if (this._cameraControls) {
        return;
      }

      // CameraControls가 없으면 기본 자동 회전 사용
      this._activeCamera.position.set(8, 5, 8);
      this._activeCamera.lookAt(this._targetPosition);
      this._cameraRotation = { x: 0, y: 0 };
    } catch (error) {
      console.error('CameraManager: 자동 회전 설정 중 오류 발생:', error);
    }
  }

  /**
   * 자동 회전 카메라 업데이트 (렌더링 루프에서 호출)
   */
  public updateAutoRotateCamera(): void {
    try {
      // CameraControls가 있으면 CameraControls 업데이트
      if (this._cameraControls && this._cameraControls.enabled) {
        this._cameraControls.update(0.016); // 약 60fps
        return;
      }

      // CameraControls가 없으면 기본 자동 회전 사용
      if (!this._autoRotate || !this._activeCamera) return;

      this._cameraRotation.y += this._autoRotateSpeed * 0.01;

      const x =
        this._targetPosition.x +
        this._autoRotateDistance *
          Math.cos(this._cameraRotation.x) *
          Math.sin(this._cameraRotation.y);
      const y =
        this._targetPosition.y + this._autoRotateDistance * Math.sin(this._cameraRotation.x);
      const z =
        this._targetPosition.z +
        this._autoRotateDistance *
          Math.cos(this._cameraRotation.x) *
          Math.cos(this._cameraRotation.y);

      this._activeCamera.position.set(x, y, z);
      this._activeCamera.lookAt(this._targetPosition);
    } catch (error) {
      console.error('CameraManager: 자동 회전 카메라 업데이트 중 오류 발생:', error);
    }
  }

  /**
   * 자동 회전 토글
   */
  public toggleAutoRotate(): void {
    this._autoRotate = !this._autoRotate;
  }

  /**
   * 자동 회전 속도 조절
   */
  public setAutoRotateSpeed(speed: number): void {
    this._autoRotateSpeed = Math.max(0.1, Math.min(10, speed));
  }

  /**
   * 자동 회전 상태 가져오기
   */
  public getAutoRotateState(): { enabled: boolean; speed: number } {
    return {
      enabled: this._autoRotate,
      speed: this._autoRotateSpeed,
    };
  }

  /**
   * 오브젝트 크기에 따른 카메라 거리 제한 동적 조정
   */
  public adjustCameraConstraintsForObject(object: THREE.Object3D): void {
    try {
      if (!this._cameraControls || !object) return;

      // 오브젝트의 바운딩 박스 계산
      const box = new THREE.Box3().setFromObject(object);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxDimension = Math.max(size.x, size.y, size.z);

      // 오브젝트 크기에 따른 거리 제한 조정
      const minDistance = Math.max(2, maxDimension * 0.8); // 오브젝트 크기의 50%
      const maxDistance = Math.max(20, maxDimension * 2.4); // 오브젝트 크기의 300%

      this._cameraControls.minDistance = minDistance;
      this._cameraControls.maxDistance = maxDistance;

      this._cameraControls.setTarget(center.x, center.y, center.z, false);

      this._cameraControls.fitToSphere(object, false);

      console.log(
        `CameraManager: 오브젝트 크기에 맞춰 카메라 제한이 조정되었습니다. (${minDistance.toFixed(2)} ~ ${maxDistance.toFixed(2)})`,
      );
    } catch (error) {
      console.error('CameraManager: 카메라 제한 동적 조정 중 오류 발생:', error);
    }
  }

  /**
   * 현재 카메라 제한 상태 조회
   */
  public getCameraConstraints(): {
    minPolarAngle: number;
    maxPolarAngle: number;
    minDistance: number;
    maxDistance: number;
  } | null {
    try {
      if (!this._cameraControls) return null;

      return {
        minPolarAngle: this._cameraControls.minPolarAngle,
        maxPolarAngle: this._cameraControls.maxPolarAngle,
        minDistance: this._cameraControls.minDistance,
        maxDistance: this._cameraControls.maxDistance,
      };
    } catch (error) {
      console.error('CameraManager: 카메라 제한 상태 조회 중 오류 발생:', error);
      return null;
    }
  }

  /**
   * 카메라 aspect ratio 업데이트
   */
  public updateCameraAspect(aspect: number): void {
    try {
      if (!this._activeCamera) return;

      if (this._activeCamera instanceof THREE.PerspectiveCamera) {
        this._activeCamera.aspect = aspect;
        this._activeCamera.updateProjectionMatrix();
      }

      console.log(
        `CameraManager: 카메라 aspect ratio가 ${aspect.toFixed(3)}으로 업데이트되었습니다.`,
      );
    } catch (error) {
      console.error('CameraManager: 카메라 aspect ratio 업데이트 중 오류 발생:', error);
    }
  }
}
